
import React, { useState } from 'react';
import { Clock, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import { db } from '../services/db';

interface LoginProps {
  onLogin: (email: string, name?: string, rate?: number) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [rate, setRate] = useState(50);
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!email) return;

    const users = db.getUsers();
    const existingUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());

    if (!isRegistering) {
      if (!existingUser) {
        setError("Account not found. Would you like to register instead?");
        return;
      }
      setIsLoading(true);
      setTimeout(() => onLogin(email), 500);
    } else {
      if (existingUser) {
        setError("An account with this email already exists. Try signing in.");
        return;
      }
      setIsLoading(true);
      setTimeout(() => onLogin(email, name || 'Jane Doe', rate), 500);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 p-8 space-y-6 animate-in fade-in zoom-in-95 duration-500 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-blue-600"></div>

        <div className="flex flex-col items-center text-center">
          <div className="bg-blue-600 p-3 rounded-2xl text-white mb-4 shadow-lg shadow-blue-500/20">
            <Clock size={28} strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">
            Timesheet <span className="text-blue-600">Pro</span>
          </h1>
          <p className="text-[11px] text-slate-500 mt-2 font-medium leading-relaxed px-4">
            {isRegistering 
              ? 'Join our community of professionals and start tracking today.' 
              : 'Welcome back. Your dashboard is ready for you.'}
          </p>
        </div>

        {error && (
          <div className="bg-amber-50 border border-amber-200 p-3 rounded-xl flex items-start gap-3 animate-in slide-in-from-top-2">
            <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-800 font-bold leading-tight">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegistering && (
            <div className="space-y-1">
              <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Full Name</label>
              <input 
                required
                type="text" 
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all text-sm"
                placeholder="e.g. Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          )}
          
          <div className="space-y-1">
            <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
            <input 
              required
              type="email" 
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all text-sm"
              placeholder="jane@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          
          <button 
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] transition-all active:scale-[0.98] flex items-center justify-center gap-2 shadow-xl shadow-blue-500/20 disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <>
                {isRegistering ? 'Create Account' : 'Secure Sign In'}
                <ArrowRight size={14} strokeWidth={3} />
              </>
            )}
          </button>
        </form>

        <div className="text-center pt-2">
          <button 
            onClick={() => {
              setIsRegistering(!isRegistering);
              setError(null);
            }}
            className="text-slate-400 font-bold hover:text-blue-600 transition text-[9px] uppercase tracking-widest"
          >
            {isRegistering ? 'Already have an account? Sign In' : "New to the platform? Register"}
          </button>
        </div>
      </div>
      
      <div className="fixed bottom-8 text-[9px] font-black text-slate-300 uppercase tracking-[0.3em]">
        Browser Local Storage Secured
      </div>
    </div>
  );
};

export default Login;
